// A permissão de saúde, por TIPO — lógica pura, sem SDK.
//
// O QUE ELA CONSERTA (issue #480, medido em 15/08): `connectHealth` decidia o
// estado da conexão com `readAuthorized.length > 0` — UM tipo concedido entre
// cinco carimbava `status='active'`. Dezesseis pessoas viam "conectado ✓" e
// não recebiam métrica nenhuma, porque o Health Connect concede POR TIPO e
// elas tinham marcado só Sono e Exercício. A permissão só era consultada no
// dia da conexão e nunca mais, então o estado ficava congelado para sempre.
//
// ⚠️ AS DUAS PLATAFORMAS NÃO QUEREM DIZER A MESMA COISA COM `readDenied`, e
// tratar como se quisessem produz UI que mente:
//
//   ANDROID (Health Connect) — `readDenied` é o que o usuário NÃO CONCEDEU.
//     Ler um tipo negado lança SecurityException. É verdade acionável: dá para
//     pular a consulta, nomear o que falta e mandar para a tela de permissão.
//
//   iOS (HealthKit) — a Apple ESCONDE recusa de leitura por design (senão o
//     app inferiria doença pela ausência do dado). O plugin classifica por
//     `getRequestStatusForAuthorization`: `.unnecessary` → autorizado,
//     `.shouldRequest` → negado (Health.swift:1376-1383). Ou seja, no iOS
//     `readDenied` significa "NUNCA FOI PERGUNTADO", e `readAuthorized`
//     significa "já foi perguntado" — inclusive quando a resposta foi NÃO.
//     Consequências que este módulo assume:
//       · lista vazia de negados NÃO garante que o dado vai fluir;
//       · tipo negado no iOS se resolve PERGUNTANDO (a folha aparece só para
//         quem nunca foi perguntado), não mandando para Ajustes;
//       · e por isso o iOS NUNCA pula a consulta — quem foi perguntado e
//         recusou aparece como autorizado, e a leitura volta vazia sem erro.

/** Os cinco tipos que o app pede. UMA lista, usada no pedido e na conferência. */
export const HEALTH_READ_TYPES = [
  "sleep",
  "heartRate",
  "heartRateVariability",
  "steps",
  "workouts",
] as const;

export type HealthReadType = (typeof HEALTH_READ_TYPES)[number];

/** Tipo do plugin → chave i18n do nome que o usuário lê. */
export const HEALTH_TYPE_LABEL_KEY: Record<HealthReadType, string> = {
  sleep: "health.type.sleep",
  heartRate: "health.type.heartRate",
  heartRateVariability: "health.type.hrv",
  steps: "health.type.steps",
  workouts: "health.type.workouts",
};

export interface PermissionVerdict {
  /** full = nada negado · partial = parte · none = nada concedido. */
  kind: "full" | "partial" | "none";
  denied: HealthReadType[];
  authorized: HealthReadType[];
  /** O valor que a coluna `status` pode receber (CHECK: pending|active|expired|revoked|error). */
  status: "active" | "error";
  /**
   * O valor de `last_sync_status`. É AQUI que a verdade parcial mora: a coluna
   * `status` tem CHECK e não aceita 'partial', e migration está fora do escopo
   * desta correção — então o estado do meio vive em `last_sync_status` +
   * `provider_metadata.read_denied`, que já existem.
   */
  lastSyncStatus: "permission_denied" | "permission_partial" | null;
}

/** Ordena e filtra para os tipos que o app conhece — plugin novo não injeta lixo na UI. */
function normalize(lista: readonly string[] | undefined): HealthReadType[] {
  const set = new Set(lista ?? []);
  return HEALTH_READ_TYPES.filter((t) => set.has(t));
}

/**
 * O veredito por tipo, a partir do que `requestAuthorization`/`checkAuthorization`
 * devolveu.
 *
 * `none` só quando NADA foi concedido — aí a conexão é 'error' de verdade e o
 * card manda reconectar. Um meio-termo continua 'active' de propósito: sono e
 * treino ESTÃO entrando, e marcar a conexão inteira como erro faria a pessoa
 * desconectar algo que funciona pela metade.
 */
export function readPermissionVerdict(status: {
  readAuthorized?: readonly string[];
  readDenied?: readonly string[];
}): PermissionVerdict {
  const authorized = normalize(status.readAuthorized);
  const denied = normalize(status.readDenied);

  if (authorized.length === 0) {
    return { kind: "none", denied, authorized, status: "error", lastSyncStatus: "permission_denied" };
  }
  if (denied.length === 0) {
    return { kind: "full", denied, authorized, status: "active", lastSyncStatus: null };
  }
  return { kind: "partial", denied, authorized, status: "active", lastSyncStatus: "permission_partial" };
}

/**
 * Quais tipos NÃO consultar nesta rodada.
 *
 * Android: pular o que está negado evita a SecurityException em todo sync e
 * deixa os tipos concedidos sincronizarem limpos — hoje o erro de passos
 * entra no `last_sync_error` das três métricas e afoga o resto.
 * iOS: NUNCA pula. Lá "negado" é "não perguntado", e quem recusou de verdade
 * aparece como autorizado — pular pela lista erraria nas duas direções.
 */
export function typesToSkip(verdict: PermissionVerdict, platform: string): HealthReadType[] {
  return platform === "android" ? verdict.denied : [];
}

/**
 * O reparo possível, por plataforma. É o que decide o botão do card.
 *
 * android — `Health.openHealthConnectSettings()`: o SO limita re-prompt, então
 *   pedir de novo pode simplesmente não abrir nada; a tela de permissão do
 *   Health Connect é o único caminho que sempre funciona.
 * ios — pedir de novo: a folha aparece para os tipos nunca perguntados, e é
 *   no-op para os demais. Não existe deep link público para a página do app
 *   dentro do Saúde (esquemas `App-Prefs:` são API privada e reprovam na
 *   revisão), então o texto diz o caminho: Ajustes > Saúde > Acesso a Dados.
 */
/**
 * Os tipos negados que o banco já guarda, lidos do `provider_metadata`.
 *
 * É o que a TELA usa: o card não chama o plugin, ele lê o que o último sync
 * registrou. Nunca lança — metadata corrompido devolve lista vazia, e lista
 * vazia significa "sem problema conhecido", que é o fallback seguro (não
 * inventa alarme).
 */
export function deniedFromMetadata(meta: unknown): HealthReadType[] {
  if (!meta || typeof meta !== "object") return [];
  const bruto = (meta as { read_denied?: unknown }).read_denied;
  if (!Array.isArray(bruto)) return [];
  return normalize(bruto.filter((v): v is string => typeof v === "string"));
}

export function repairPath(platform: string): "open_health_connect" | "request_again" | "none" {
  if (platform === "android") return "open_health_connect";
  if (platform === "ios") return "request_again";
  return "none";
}
